import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import axios from "axios";
import { useAuth } from "../../../layouts/auth/AuthContext";
import { FetchApi, FetchApiNoParam, apiClient } from "../../../utils/api";

export interface Room {
  RoomId: string;
  RoomName: string;
  RoomDescription: string;
  RoomQuantity: number;
  occupancy: number;
  Amenities: RoomAmenity[];
  Addon: RoomAddon[];
  RoomPicture: string[];
  RoomPrice: RoomPrice;
}

export interface RoomAmenity {
  roomfacilities: string;
  roomfacilities_pic: string;
}

export interface RoomAddon {
  addon_desc: string;
  addon_price: string;
  addon_code: string;
}

export interface RoomPrice {
  per_room_OriginalPrice: string | null;
  per_room_NonrefundablePrice: string | null;
  total_OriginalPrice: number;
  total_NonrefundablePrice: number;
  DiscountPrice: number;
  DiscountRate: string;
  DiscountType?: string;
  special?: SpecialRate[];
  rateplan?: RatePlan[];
}

export interface SpecialRate {
  title: string;
  amount: string;
  id: string;
}

export interface RatePlan {
  id: string;
  title: string;
  amount: string;
  refundable?: boolean;
  pay_later?: boolean;
  breakfast?: boolean;
  [key: string]: any;
}

export interface SelectedRate {
  type: "standard" | "special" | "rateplan";
  // legacy name used in some places
  rate?: SpecialRate;
  // explicit fields for rateplan flows
  rateplan?: RatePlan;
  specialRate?: SpecialRate;
}

export interface CurrentRoomBookingDetails {
  selectedRoom: Room;
  selectedRate?: SelectedRate; // Add this line
  bookingParams: {
    roomQty: string;
    checkInDate: string;
    checkOutDate: string;
    name: string;
    email: string;
    phone: string;
    specialInstruction: string;
    childPax: number;
    adultPax: number;
  };
  priceType: any;
}

interface RoomBooking {
  booking_date: string | number | Date;
  checkin_date(checkin_date: any, checkout_date: any): unknown;
  checkout_date(checkin_date: any, checkout_date: any): unknown;
  order_number: null | undefined;
  room_type: ReactNode;
  adult_pax: ReactNode;
  children_pax: number;
  room_quantity: ReactNode;
  Addon: boolean;
  total_pay(total_pay: any): unknown;
  pay_type: ReactNode;
  voucher_title: string;
  status: string;
  usedate: string | null;
  valid_date: string;
  voucher_code: string;
  create_date: string;
  remark: string;
  orderid: string;
  merge_id: string | null;
  payment_type: string | null;
  payment_ref: string | null;
  store_id: string | null;
  store_name: string;
  amount: string | null;
  price_title?: string;
}

// Types for Context Value
interface RoomContextType {
  companyDetails: Record<string, any>;
  generalLoading: boolean;
  generalError: any;

  loading: boolean;
  error: string | null;

  bookings: any[];
  bookingLoading: boolean;
  bookingError: any;

  rooms: Room[];
  searching: boolean;

  fetchCompanyDetails: () => Promise<any>;
  fetchImageSlides: () => Promise<any>;
  fetchStates: () => Promise<any>;
  fetchTables: (params?: any) => Promise<any[]>;
  fetchRestaurants: () => Promise<any>;
  fetchCountries: () => Promise<any>;
  fetchRooms: (params?: any) => Promise<Room[]>;

  fetchBookings: (params?: any) => Promise<any[]>;
  singleBooking: (params?: any) => Promise<any>;
  createBooking: (params: any, addonParams: any[]) => Promise<any>;
  createPayment: (params: any) => Promise<string>;
  checkPaymentStatus: (params: any) => Promise<any>;

  currentRoomBookingDetails: CurrentRoomBookingDetails;
  onCurrentRoomBookingDetails: (booking: any) => void;

  roomBookings: RoomBooking[];
  fetchRoomBookings: (parameters: { UUID: string }) => Promise<RoomBooking[]>;
}

const RoomContext = createContext<RoomContextType | null>(null);

interface RoomProviderProps {
  children: ReactNode;
}

export const RoomProvider: React.FC<RoomProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // General State
  const [companyDetails, setCompanyDetails] = useState<Record<string, any>>({});
  const [generalLoading, setGeneralLoading] = useState<boolean>(false);
  const [generalError, setGeneralError] = useState<any>(null);

  // Booking State
  const [bookings, setBookings] = useState<any[]>([]);
  const [bookingLoading, setBookingLoading] = useState<boolean>(false);
  const [bookingError, setBookingError] = useState<any>(null);

  // Room Search State
  const [rooms, setRooms] = useState<Room[]>([]);
  const [searching, setSearching] = useState<boolean>(false);

  const [roomBookings, setRoomBookings] = useState<RoomBooking[]>([]);

  // Temp Booking
  const [currentRoomBookingDetails, setCurrentRoomBookingDetails] =
    useState<any>({});
  const onCurrentRoomBookingDetails = (booking: any) =>
    setCurrentRoomBookingDetails(booking);

  // General Actions
  const fetchCompanyDetails = useCallback(async (): Promise<any> => {
    setGeneralLoading(true);
    setGeneralError(null);
    try {
      const url = FetchApiNoParam("Company_Details.php");
      const response = await apiClient.get(url);
      if (response.data) {
        setCompanyDetails(response.data);
        return response.data;
      } else {
        setCompanyDetails({});
        throw response.data;
      }
    } catch (err) {
      setGeneralError(err);
      setCompanyDetails({});
      throw err;
    } finally {
      setGeneralLoading(false);
    }
  }, []);

  const fetchImageSlides = useCallback(async (): Promise<any> => {
    setGeneralLoading(true);
    setGeneralError(null);
    try {
      const url = FetchApiNoParam("Image_Slider.php");
      const response = await apiClient.get(url);
      if (response.data) return response.data;
      throw response.data;
    } catch (err) {
      setGeneralError(err);
      throw err;
    } finally {
      setGeneralLoading(false);
    }
  }, []);

  const fetchStates = useCallback(async (): Promise<any> => {
    setGeneralLoading(true);
    setGeneralError(null);
    try {
      const url = FetchApiNoParam("state.php");
      const response = await apiClient.get(url);
      if (response.data) return response.data;
      throw response.data;
    } catch (err) {
      setGeneralError(err);
      throw err;
    } finally {
      setGeneralLoading(false);
    }
  }, []);

  const fetchTables = useCallback(async (parameters?: any): Promise<any[]> => {
    setGeneralLoading(true);
    setGeneralError(null);
    try {
      const url = FetchApi("table_search.php", {
        name: parameters?.name,
        date: parameters?.date,
        voucher_code: parameters?.voucher_code || "",
      });
      const response = await apiClient.get(url);
      if (response.data) return [response.data];
      throw response.data;
    } catch (err) {
      setGeneralError(err);
      throw err;
    } finally {
      setGeneralLoading(false);
    }
  }, []);

  const fetchRestaurants = useCallback(async (): Promise<any> => {
    setGeneralLoading(true);
    setGeneralError(null);
    try {
      const url = FetchApiNoParam("restaurant_list.php");
      const response = await apiClient.get(url);
      if (response.data) return response.data;
      throw response.data;
    } catch (err) {
      setGeneralError(err);
      throw err;
    } finally {
      setGeneralLoading(false);
    }
  }, []);

  const fetchCountries = useCallback(async (): Promise<any> => {
    setGeneralLoading(true);
    setGeneralError(null);
    try {
      const url = FetchApiNoParam("country.php");
      const response = await apiClient.get(url);
      if (response.data) return response.data;
      throw response.data;
    } catch (err) {
      setGeneralError(err);
      throw err;
    } finally {
      setGeneralLoading(false);
    }
  }, []);

  const fetchRooms = useCallback(async (parameters?: any): Promise<Room[]> => {
    setSearching(true);
    setGeneralError(null);
    try {
      const url = FetchApi("Room_Search.php", parameters);
      const response = await apiClient.get(url);
      if (response.data[0]?.status === undefined) {
        console.log("check response.data ===> ", response.data);
        setRooms(response.data);
        return response.data;
      } else {
        setRooms([]);
        throw response.data;
      }
    } catch (err) {
      setGeneralError(err);
      setRooms([]);
      throw err;
    } finally {
      setSearching(false);
    }
  }, []);

  // Booking Actions
  const fetchBookings = useCallback(
    async (parameters?: any): Promise<any[]> => {
      setBookingLoading(true);
      setBookingError(null);
      try {
        const url = FetchApi("list_booking.php", parameters);
        const response = await apiClient.get(url);
        if (response?.data[0]?.status === undefined) {
          setBookings(response.data);
          return response.data;
        } else {
          setBookings([]);
          throw response.data;
        }
      } catch (err) {
        setBookingError(err);
        setBookings([]);
        throw err;
      } finally {
        setBookingLoading(false);
      }
    },
    []
  );

  const fetchRoomBookings = async (parameters: {
    UUID: string;
  }): Promise<RoomBooking[]> => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("list_booking.php", parameters);
      console.log("URL ======= fetchRoomBookings", url);
      const response = await apiClient.get(url);

      if (response.data) {
        console.log("Valid Response ======= fetchRoomBookings", response.data);

        // Ensure we always set an array, even if response is empty
        const bookings = Array.isArray(response.data) ? response.data : [];
        setRoomBookings(bookings);
        return bookings;
      } else {
        console.log(
          "Invalid Response ======= fetchRoomBookings",
          response.data
        );
        const errorMsg = "Invalid response from server";
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.log("Catch Error ======= fetchRoomBookings", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch bookings";
      setError(errorMsg);

      // Set empty array on error to prevent crashes
      setRoomBookings([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const singleBooking = useCallback(async (parameters?: any): Promise<any> => {
    setBookingLoading(true);
    try {
      const url = FetchApi("single_booking.php", parameters);
      const response = await apiClient.get(url);
      return response.data[0];
    } catch (err) {
      setBookingError(err);
      throw err;
    } finally {
      setBookingLoading(false);
    }
  }, []);

  const createBooking = useCallback(
    async (parameters: any, addonParameters: any[]): Promise<any> => {
      setBookingLoading(true);
      try {
        const addonParams = addonParameters
          .map(
            (i, idx) =>
              `addon[${idx}][addon_code]=${i.addon_code}&addon[${idx}][pax]=${i.pax}&addon[${idx}][night]=${i.night}`
          )
          .join("&");
        const url =
          FetchApi("create_booking.php", parameters) + "&" + addonParams;
        console.log("check url createBooking ===> ", url);
        const response = await apiClient.get(url);
        console.log("check response createBooking ===> ", response);
        if (response.data[0].status === 1) return response.data[0];
        throw response.data[0];
      } catch (err) {
        setBookingError(err);
        throw err;
      } finally {
        setBookingLoading(false);
      }
    },
    []
  );

  const createPayment = useCallback(
    async (parameters: any): Promise<string> => {
      try {
        const url = FetchApi("ipay", parameters);
        return url;
      } catch (err) {
        setBookingError(err);
        throw err;
      }
    },
    []
  );

  const checkPaymentStatus = useCallback(
    async (parameters: any): Promise<any> => {
      setBookingLoading(true);
      try {
        const url = FetchApi("payment_status.php", parameters);
        const response = await apiClient.get(url);
        return response.data;
      } catch (err) {
        setBookingError(err);
        throw err;
      } finally {
        setBookingLoading(false);
      }
    },
    []
  );

  const value: RoomContextType = {
    companyDetails,
    generalLoading,
    generalError,
    bookings,
    bookingLoading,
    bookingError,
    rooms,
    searching,
    fetchCompanyDetails,
    fetchImageSlides,
    fetchStates,
    fetchTables,
    fetchRestaurants,
    fetchCountries,
    fetchRooms,
    fetchBookings,
    singleBooking,
    createBooking,
    createPayment,
    checkPaymentStatus,
    currentRoomBookingDetails,
    onCurrentRoomBookingDetails,
    roomBookings,
    fetchRoomBookings,
    loading,
    error,
  };

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
};

export const useRoom = (): RoomContextType => {
  const context = useContext(RoomContext);
  if (!context) throw new Error("useRoom must be used within a RoomProvider");
  return context;
};
